import type { DataTableColumn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import { Classeur, type FilterDef } from "../components/classeur";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";
import { Rang1OnlyNotice } from "../components/rang1-only-notice";

type PendingReportItem = {
  id: string;
  numero: string | null;
  dateReception: string | null;
  dateRedaction: string | null;
  contractCode: string | null;
  noticeCount: number;
  remarkCount: number;
  fichierId: number | null;
  hasPdf: boolean;
  piloted: boolean;
};

type Emitter = {
  contractId: number;
  contractCode: string | null;
  count: number;
};

const formatDate = (value: unknown) =>
  typeof value === "string" ? formatDay(value) : "-";

const EMETTEUR_FILTER_KEY = "emetteur";

export function ReceptionRapportsSas() {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useNavigate();
  const { hasContractType } = useYardContext();
  const [state, setState] = useClasseurState<PendingReportItem>();
  const [emitters, setEmitters] = useState<Emitter[]>([]);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: EMETTEUR_FILTER_KEY,
        label: "Donneur d'ordre",
        options: emitters.map((e) => ({
          value: String(e.contractId),
          label: `${e.contractCode ?? "—"} (${e.count})`,
        })),
      },
    ],
    [emitters],
  );

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const emetteurContractId = activeFilters[EMETTEUR_FILTER_KEY]?.[0];

  const viewPdf = useCallback(
    async (fichierId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fichierId) } } },
      );
      if (isNotNil(data)) {
        window.open(data.data.inlineUrl, "_blank", "noopener,noreferrer");
      }
    },
    [yardId],
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      setError(null);
      try {
        const { data } = await apiClient.GET(
          "/api/yards/{yardId}/reports/pending",
          {
            params: {
              path: { yardId: yardId ?? "" },
              query: isNotNil(emetteurContractId) ? { emetteurContractId } : {},
            },
          },
        );
        if (cancelled) return;
        if (isNotNil(data)) {
          const items = data.data.items.map((item) => ({
            ...item,
            id: String(item.id),
          }));
          setState({ items, total: data.data.total, isLoading: false });
          setEmitters(data.data.emitters);
        } else {
          setState({ items: [], total: 0, isLoading: false });
          setError("Impossible de charger les rapports en attente.");
        }
      } catch {
        if (cancelled) return;
        setState({ items: [], total: 0, isLoading: false });
        setError("Impossible de charger les rapports en attente.");
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [yardId, emetteurContractId, setState]);

  const columns = useMemo<DataTableColumn<PendingReportItem>[]>(
    () => [
      { id: "numero", header: "N°", accessor: "numero" },
      {
        id: "dateReception",
        header: "Date réception",
        accessor: "dateReception",
        cell: ({ value }) => formatDate(value),
      },
      {
        id: "dateRedaction",
        header: "Date rédaction",
        accessor: "dateRedaction",
        cell: ({ value }) => formatDate(value),
      },
      {
        id: "contractCode",
        header: "Donneur d'ordre",
        accessor: "contractCode",
      },
      { id: "noticeCount", header: "Avis", accessor: "noticeCount" },
      { id: "remarkCount", header: "Observations", accessor: "remarkCount" },
      {
        id: "pdf",
        header: "PDF",
        accessor: "hasPdf",
        cell: ({ row }) =>
          row.hasPdf && isNotNil(row.fichierId) ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                viewPdf(row.fichierId as number);
              }}
              className="text-red-600 hover:text-red-700"
              aria-label={`Ouvrir le PDF du rapport ${row.numero ?? ""}`}
            >
              <FilePdfIcon size={18} />
            </button>
          ) : (
            <span className="text-zinc-300">-</span>
          ),
      },
    ],
    [viewPdf],
  );

  const emptyMessage = isNotNil(error)
    ? error
    : "Aucun rapport en attente de traitement";

  return (
    <Classeur
      title="Réception des rapports d'examen (sas donneurs d'ordre)"
      items={state.items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage={emptyMessage}
      total={state.total}
      notice={
        hasContractType("travaux_n_1") ? undefined : (
          <Rang1OnlyNotice>
            Seuls les pilotes de contrat travaux rang 1 traitent ces rapports :
            vous pouvez les ouvrir en consultation.
          </Rang1OnlyNotice>
        )
      }
      onRowClick={(row) =>
        navigate(`/yards/${yardId}/traiter-observations?rapport=${row.id}`)
      }
      getRowClassName={(row) =>
        row.piloted ? "bg-blue-50 font-medium dark:bg-blue-900/20" : undefined
      }
    />
  );
}
